import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Stack,
    Typography,
    InputBase,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import FormInput from '../../../inputs/FormInput';
import { normaliseHex, buildPresetFromHex } from '../../../../setting/presets';

/**
 * Hex color picker for the custom preset slot.
 *
 * Two ways to pick:
 *   1. Native <input type="color"> for visual selection
 *   2. Hex text field with live validation
 *
 * On Save the (validated, lowercase) hex is handed back to the parent
 * which feeds it to onChangeCustomColor in SettingsContext.
 */
const CustomColorDialog = ({ open, initialHex, onClose, onConfirm }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const [hex, setHex] = useState(initialHex || '#00A76F');
    const [textInput, setTextInput] = useState(initialHex || '#00A76F');

    useEffect(() => {
        if (open) {
            const normalised = normaliseHex(initialHex) || '#00A76F';
            setHex(normalised);
            setTextInput(normalised);
        }
    }, [open, initialHex]);

    const isValid = Boolean(normaliseHex(hex));
    const preview = buildPresetFromHex(hex) || buildPresetFromHex('#00A76F');

    const handleColorPick = (e) => {
        const val = e.target.value;
        setHex(val);
        setTextInput(val);
    };

    const handleTextChange = (e) => {
        const val = e.target.value;
        setTextInput(val);
        const normalised = normaliseHex(val);
        if (normalised) setHex(normalised);
    };

    const handleConfirm = () => {
        const normalised = normaliseHex(hex);
        if (!normalised) return;
        onConfirm(normalised);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontWeight: 700 }}>
                {isArabic ? 'لون مخصص' : 'Custom color'}
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2.5} sx={{ pt: 1 }}>
                    {/* Visual picker — wraps a styled square around native input[type=color] */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <Box
                            sx={{
                                position: 'relative',
                                width: 96,
                                height: 96,
                                borderRadius: 3,
                                border: (t) => `1.5px solid ${t.palette.divider}`,
                                overflow: 'hidden',
                                bgcolor: hex,
                                boxShadow: `0 8px 16px 0 ${hex}55`,
                                cursor: 'pointer',
                            }}
                        >
                            <InputBase
                                type="color"
                                value={hex}
                                onChange={handleColorPick}
                                inputProps={{
                                    'aria-label': 'Pick custom color',
                                    style: {
                                        position: 'absolute',
                                        inset: 0,
                                        opacity: 0,
                                        cursor: 'pointer',
                                        width: '100%',
                                        height: '100%',
                                        padding: 0,
                                        border: 'none',
                                    },
                                }}
                            />
                        </Box>
                    </Box>

                    <FormInput
                        type="text"
                        name="hex"
                        label={isArabic ? 'كود اللون (HEX)' : 'Hex code'}
                        value={textInput}
                        onChange={handleTextChange}
                        placeholder="#00A76F"
                        required={false}
                        error={textInput && !isValid
                            ? (isArabic ? 'كود غير صالح. مثال: #00A76F' : 'Invalid hex. Example: #00A76F')
                            : undefined}
                    />

                    {/* Live preview swatches */}
                    {isValid && (
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                                {isArabic ? 'معاينة' : 'Preview'}
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                {[
                                    { key: 'light', value: preview.light, en: 'Light', ar: 'فاتح' },
                                    { key: 'main', value: preview.main, en: 'Main', ar: 'أساسي' },
                                    { key: 'dark', value: preview.dark, en: 'Dark', ar: 'داكن' },
                                ].map(({ key, value, en, ar }) => (
                                    <Box
                                        key={key}
                                        sx={{
                                            flex: 1,
                                            borderRadius: 1.5,
                                            border: (t) => `1px solid ${t.palette.divider}`,
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <Box sx={{ height: 40, bgcolor: value }} />
                                        <Box sx={{ px: 0.75, py: 0.5 }}>
                                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                                                {isArabic ? ar : en}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                                {value}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} color="inherit">
                    {isArabic ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button variant="contained" onClick={handleConfirm} disabled={!isValid}>
                    {isArabic ? 'حفظ' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CustomColorDialog;
